import { computed, ref, toValue, watch, type MaybeRefOrGetter, type Ref } from 'vue';
import type { PageOrientation } from '@record-fill';
import { computePageCount, computePageHeight } from '@/features/FormBuilder';

export function computeCurrentPage(
  scrollTop: number,
  pageHeight: number,
  zoom: number,
  pageCount: number
): number {
  const scaledPage = pageHeight * zoom;
  if (scaledPage <= 0) return 1;
  return Math.min(pageCount, Math.floor(scrollTop / scaledPage) + 1);
}

interface SheetPagesInput {
  sheetRef: Ref<HTMLElement | null>;
  scrollRef: Ref<HTMLElement | null>;
  displayWidth: Ref<number>;
  zoom: Ref<number>;
  orientation?: MaybeRefOrGetter<PageOrientation>;
}

export function useSheetPages({
  sheetRef,
  scrollRef,
  displayWidth,
  zoom,
  orientation = 'portrait',
}: SheetPagesInput) {
  const sheetHeight = ref(0);
  const atEnd = ref(false);
  const scrollTop = ref(0);

  const pageHeight = computed(() => computePageHeight(displayWidth.value, toValue(orientation)));
  const pageCount = computed(() => computePageCount(sheetHeight.value, pageHeight.value));
  const currentPage = computed(() =>
    atEnd.value
      ? pageCount.value
      : computeCurrentPage(scrollTop.value, pageHeight.value, zoom.value, pageCount.value)
  );
  const scaledHeight = computed(() => sheetHeight.value * zoom.value);
  const scaledWidth = computed(() => displayWidth.value * zoom.value);

  // The sheet is one continuous flow, so its last page is usually shorter than a page: the scroll
  // passes the end of the sheet before scrollTop reaches the last page boundary, and only "the end of
  // the sheet is in view" can say so. It is measured on the sheet, never on the end of the scroll:
  // the attachments sit below the sheet in the same scroll.
  function sheetEndInView(container: HTMLElement): boolean {
    const sheet = sheetRef.value;
    if (!sheet) return false;
    const sheetBox = sheet.getBoundingClientRect();
    const visibleBottom =
      container.getBoundingClientRect().top + container.clientTop + container.clientHeight;
    return sheetBox.height > container.clientHeight && sheetBox.bottom <= visibleBottom + 1;
  }

  function onScroll(): void {
    const element = scrollRef.value;
    scrollTop.value = element?.scrollTop ?? 0;
    atEnd.value = element !== null && sheetEndInView(element);
  }

  // offsetHeight ignores the CSS transform, so the zoom never feeds back into the measurement.
  watch(
    sheetRef,
    (element, _previous, onCleanup) => {
      if (!element) {
        sheetHeight.value = 0;
        return;
      }
      sheetHeight.value = element.offsetHeight;
      const observer = new ResizeObserver(() => {
        sheetHeight.value = element.offsetHeight;
      });
      observer.observe(element);
      onCleanup(() => observer.disconnect());
    },
    { immediate: true }
  );

  return { sheetHeight, pageHeight, pageCount, currentPage, scaledHeight, scaledWidth, onScroll };
}
